import {
  AfterViewInit,
  Component,
  OnDestroy,
  effect,
  inject,
  isDevMode,
  output,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink, RouterModule } from '@angular/router';
import {
  InfiniteScrollCustomEvent,
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonFab,
  IonHeader,
  IonIcon,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPopover,
  IonRouterLink,
  IonSearchbar,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
  ScrollBaseDetail,
} from '@ionic/angular/standalone';
import { VxErrorComponent } from '@libs/core/lib/error-view/error-view.component';
import { NgEventBus } from '@libs/core/lib/event-bus/ng-event-bus';
import { VxDynamicImageComponent } from '@libs/core/lib/shared/mtjr-image.component';

import { withProductsList } from '@libs/mtjr/shared/generated/entities/product/product-store-features';
import { patchState, signalStore, withComputed, withMethods } from '@ngrx/signals';
import { Subscription, throttleTime } from 'rxjs';

import { BASE_CONFIG } from '@libs/core/base-config';
import { VxBlurImgComponent } from '@libs/core/lib/shared/mtjr-blur-image.component';
import { dataFromState, initialState } from '@libs/core/lib/state/async-states';
import { AppAlerts } from '@libs/core/lib/utils/app-alerts';
import { withGetVendorById, withVendorDelete } from '@libs/mtjr/shared/generated/entities/vendor/vendor-store-features';
import { Dtos, Entity } from '@libs/mtjr/shared/generated/types';
import { VxProductCardComponent } from '@libs/mtjr/shared/products/product_card.component';
import { VxVoucherCardComponent } from '@libs/mtjr/shared/products/voucher-card.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { VendorFilterSheetComponent } from './vendor-filter-sheet.component';

export const VendorDetailsStore = signalStore(
  withProductsList(),
  withGetVendorById(),
  withVendorDelete(),
  withComputed((store) => ({
    vendor: dataFromState(store.getVendorByIdState),
  })),
  withMethods((store) => ({
    resetVendorDeleteState() {
      patchState(store, { vendorDeleteState: initialState() });
    },
    resetProductsListState() {
      patchState(store, {
        productsListState: initialState(),
        productsListInput: { page: 1, totalPerPage: 20 },
        productsListCanFetchNext: false,
        productsListFetchingNext: false,
      });
    },
  })),
);
@Component({
  selector: 'mtjr-vendor-details',
  templateUrl: './vendor-details.page.html',
  providers: [VendorDetailsStore],
  imports: [
    IonInfiniteScroll,
    IonItem,
    IonPopover,
    IonSpinner,
    IonFab,
    IonLabel,
    IonButton,
    IonBackButton,
    IonButtons,
    IonToolbar,
    IonContent,
    IonIcon,
    RouterLink,
    IonHeader,
    RouterModule,
    IonSearchbar,
    TranslatePipe,
    VxErrorComponent,
    IonModal,
    VxDynamicImageComponent,
    VendorFilterSheetComponent,
    IonInfiniteScroll,
    IonInfiniteScrollContent,
    VxProductCardComponent,
    VxVoucherCardComponent,
    VxBlurImgComponent,
    IonText,
    IonTitle,
    IonRouterLink,
    IonList,
  ],
})
export class VendorDetailsPage implements OnDestroy, AfterViewInit {
  variationAddToCart = output<{ product: Entity.Product; variation: Dtos.ProductVariation }>();
  isDev = isDevMode();
  store = inject(VendorDetailsStore);
  route = inject(ActivatedRoute);
  eventBus = inject(NgEventBus);
  searchValue = signal<string | undefined>(undefined);
  vendorId = this.route.snapshot.params['vendorId'] as string;
  router = inject(Router);
  sub?: Subscription;
  routeSub?: Subscription;
  scrollSub?: Subscription;
  ionPopovers = viewChildren(IonPopover, { read: IonPopover });
  ionModal = viewChild(IonModal, { read: IonModal });
  ionContent = viewChild(IonContent, { read: IonContent });
  alerts = inject(AppAlerts);
  translate = inject(TranslateService);
  isHeaderCollapsed = signal(false);
  private scrollThreshold = 0.5;

  filterModalQuery = signal<Dtos.ProductsListQueryParams | undefined>(undefined);
  shareModalOpen = signal(false);
  currentShareUrl = signal('');

  private readonly baseConfig = inject(BASE_CONFIG);
  isVendorOwner = this.baseConfig.flavor === 'vendors';
  shareBaseUrl = this.baseConfig.shareBaseUrl;

  constructor() {
    this.store.getVendorById({}, { vendorId: this.vendorId });
    this.sub = this.eventBus.on('products:fetch').subscribe(() => {
      this.store.productsListPage({ vendorId: this.vendorId, totalPerPage: 50, sort: 'name.ar' });
    });

    this.eventBus.on<{ vendorId?: string }>('vendors:fetch').subscribe((event) => {
      if (event.data?.vendorId === this.vendorId) {
        this.store.getVendorById({}, { vendorId: this.vendorId });
      }
    });

    effect(() => {
      const query = this.store.productsListInput();
      this.router.navigate([], {
        queryParams: {
          search: query.search ? query.search.trim() : undefined,
        },
      });
    });

    this.routeSub = this.route.queryParams.subscribe((params) => {
      const { search, action } = params;
      this.searchValue.set(search);
      const query = this.store.productsListInput();
      if (this.store.productsListState().type === 'initial' || search !== query.search) {
        this.store.productsListPage({ search, vendorId: this.vendorId, sort: 'name.ar', totalPerPage: 50 });
      }
      if (action === 'filter') {
        this.filterModalQuery.set(query);
      } else {
        this.filterModalQuery.set(undefined);
      }
    });

    effect(() => {
      if (this.filterModalQuery()) {
        this.router.navigate([], {
          queryParams: { action: 'filter' },
          queryParamsHandling: 'merge',
        });
      } else {
        this.router.navigate([], {
          queryParams: { action: undefined },
          queryParamsHandling: 'merge',
        });
      }
    });
  }

  ngAfterViewInit() {
    const content = this.ionContent();
    if (content) {
      // content.ionScrollStart.subscribe((event: any) => {
      //   console.log((event.target as any).detail);
      // });
      content.ionScrollEnd.subscribe((event: CustomEvent<ScrollBaseDetail>) => {
        const scrollTop = (event.target as any).detail.scrollTop;
        if (scrollTop < this.scrollThreshold && this.isHeaderCollapsed()) {
          this.isHeaderCollapsed.set(false);
        }
      });
      this.scrollSub = content.ionScroll
        .pipe(
          throttleTime(200), // ~60fps
        )
        .subscribe((event: any) => {
          const scrollTop = event.detail.scrollTop;
          console.log(scrollTop);
          this.isHeaderCollapsed.set(scrollTop > this.scrollThreshold);
        });
    }
  }

  reloadProductsList() {
    this.store.resetProductsListState();
    this.store.productsListPage({ vendorId: this.vendorId, totalPerPage: 50, sort: 'name.ar' });
  }

  handleSearchInput(event?: CustomEvent) {
    const search: string | undefined = event?.detail?.value;
    if (this.store.productsListInput().search === search) return;
    this.store.productsListPage({ search: search?.trim(), totalPerPage: 50 });
  }

  async onIonInfinite(event: InfiniteScrollCustomEvent) {
    await this.store.productsListFetchNextPage();
    await event?.target?.complete();
  }

  ionViewWillLeave() {
    // Dismiss any open modals/popovers
    this.ionPopovers().forEach((popover) => {
      popover.dismiss();
    });

    this.filterModalQuery.set(undefined);
    this.ionModal()?.dismiss();
  }

  submitQuery(query?: Dtos.ProductsListQueryParams) {
    if (query) {
      const existingQuery = this.store.productsListInput();
      const isDifferent = Object.keys(query).some((key) => {
        const queryKey = key as keyof Dtos.ProductsListQueryParams;
        return query[queryKey] !== existingQuery[queryKey];
      });
      if (isDifferent) {
        this.store.productsListPage({ ...query, page: 1 });
      }
    }
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
    this.routeSub?.unsubscribe();
    this.scrollSub?.unsubscribe();
  }

  async openShareOptions(vendorId: string) {
    const shareUrl = `${this.shareBaseUrl}/v/${vendorId}`;
    this.currentShareUrl.set(shareUrl);
    this.openShareModal();
  }

  openShareModal() {
    this.shareModalOpen.set(true);
  }

  copyToClipboard(text: string) {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        this.alerts.successSnackbarTR('COMMON.LINK_COPIED', { duration: 2000 });
      })
      .catch(() => {
        this.alerts.snackbarTR('COMMON.COPY_FAILED', { duration: 2000 });
      });
  }

  async shareToSocialMedia(platform?: string, url?: string) {
    // If called without parameters, use vendor share URL
    if (!platform || !url) {
      await this.openShareOptions(this.vendorId);
      return;
    }

    // Input validation
    if (!platform || !url) {
      this.alerts.snackbarTR('COMMON.INVALID_SHARE_PARAMETERS');
      return;
    }

    // Validate URL format
    try {
      new URL(url);
    } catch {
      this.alerts.snackbarTR('COMMON.INVALID_URL');
      return;
    }

    const vendor = this.store.vendor();
    if (!vendor) {
      this.alerts.snackbarTR('COMMON.VENDOR_NOT_FOUND');
      return;
    }

    // Check if Web Share API is available (mobile optimization)
    if (navigator.share && platform === 'native') {
      try {
        await navigator.share({
          title: vendor.name,
          text: `Check out ${vendor.name}`,
          url: url,
        });
        return;
      } catch (error) {
        // User cancelled or error occurred, fall back to URL sharing
        console.warn('Web Share API failed:', error);
      }
      return;
    }

    const encodedUrl = encodeURIComponent(url);
    const title = encodeURIComponent(vendor.name || '');
    const description = encodeURIComponent(`Check out ${vendor.name}`);

    let shareUrl = '';

    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case 'twitter': {
        // X/Twitter - properly format text with spacing
        const tweetText = `${title} - ${description}`;
        shareUrl = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodeURIComponent(tweetText)}`;
        break;
      }
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(`${vendor.name} ${url}`)}`;
        break;
      case 'telegram':
        shareUrl = `https://t.me/share/url?url=${encodedUrl}&text=${title}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
        break;
      default:
        this.alerts.snackbarTR('COMMON.UNSUPPORTED_PLATFORM');
        return;
    }

    if (shareUrl) {
      try {
        const newWindow = window.open(shareUrl, '_blank', 'noopener,noreferrer');
        if (!newWindow) {
          // Popup blocked - provide fallback
          this.copyToClipboard(url);
          this.alerts.snackbarTR('COMMON.POPUP_BLOCKED_LINK_COPIED');
        }
      } catch (error) {
        console.error('Failed to open share window:', error);
        // Fallback to copying link
        this.copyToClipboard(url);
        this.alerts.snackbarTR('COMMON.SHARE_FAILED_LINK_COPIED');
      }
    }
  }
}
